import React, { useEffect, useRef, useState } from 'react';
import { ILocations } from 'utils/models/locations';
import TestKitsDropDownMultiselect from '../drop-down/TestKitsDropdownMultiSelectExport';
import ExportDatePicker from '../date-picker/ExportDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import RadioButtonsGroup from '../radio/RadioGroup';
import LocationsMultiSelect from '../drop-down/LocationsMultiselect';

interface ExportProps {
	locations: ILocations[];
	testKits: any[];
	setFilters: React.Dispatch<React.SetStateAction<any>>;
	filters: any;
	setTestKits?: (e: any) => void;
	filterErrors: any;
	resetErrors: () => void;
	isViewAll: boolean;
}

const ExportResultsFilter: React.FC<ExportProps> = ({
	locations,
	testKits,
	setFilters,
	filters,
	setTestKits,
	filterErrors,
	resetErrors,
	isViewAll,
}) => {
	const [fromDate, setFromDate] = useState<Dayjs | null>(
		filters.fromDate ? dayjs(filters.fromDate) : null
	);
	const [endDate, setEndDate] = useState<Dayjs | null>(
		filters.endDate ? dayjs(filters.endDate) : null
	);
	const [selectedDateRange, setSelectedDateRange] = useState<number>(
		isViewAll ? 7300 : 0
	);
	const today = dayjs().endOf('day');
	const lastSevenDays = dayjs().startOf('day');

	useEffect(() => {
		if (isViewAll) {
			const allTimeStart = dayjs().subtract(7300, 'day').startOf('day');
			const allTimeEnd = dayjs().endOf('day');

			setFromDate(allTimeStart);
			setEndDate(allTimeEnd);
			setSelectedDateRange(7300);
			setFilters((prevFilters: any) => ({
				...prevFilters,
				fromDate: allTimeStart.valueOf(),
				endDate: allTimeEnd.valueOf(),
				dates: 7300,
			}));
		} else {
			setFromDate(lastSevenDays);
			setEndDate(today);
			setSelectedDateRange(0);
			setFilters((prevFilters: any) => ({
				...prevFilters,
				fromDate: lastSevenDays.valueOf(),
				endDate: today.valueOf(),
				dates: 0,
			}));
		}
	}, [isViewAll]);
	
	const hasInitialized = useRef(false);

	const radioButtons = [
		{ value: 0, label: 'Today' },
		{ value: 7, label: 'Last 7 days' },
		{ value: 30, label: 'Last 30 days' },
		{ value: 7300, label: 'All time' },
		{ value: -1, label: 'Custom' },
	];

	const handleFromDateChange = (newValue: unknown) => {
		if (dayjs.isDayjs(newValue) || newValue === null) {
			setFromDate(newValue ? (newValue as Dayjs).startOf('day') : null);
			setFilters((prevFilters: Dayjs | null) => ({
				...prevFilters,
				fromDate: newValue ? (newValue as Dayjs).startOf('day').valueOf() : null,
				dates: -1,
			}));
			setSelectedDateRange(-1);
		}
	};

	const handleEndDateChange = (newValue: unknown) => {
		if (dayjs.isDayjs(newValue) || newValue === null) {
			setEndDate(newValue ? (newValue as Dayjs).endOf('day') : null);
			setFilters((prevFilters: Dayjs | null) => ({
				...prevFilters,
				endDate: newValue ? (newValue as Dayjs).endOf('day').valueOf() : null,
				dates: -1,
			}));
			setSelectedDateRange(-1);
		}
	};

	const handleCheckBoxChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const days = parseInt(event.target.value, 10);
		if (days < 0) {
			setFilters((prevFilters: Dayjs | null) => ({
				...prevFilters,
				dates: days,
			}));
		} else {
			const today = dayjs().endOf('day');
			const startDate = today.subtract(days, 'day').startOf('day');

			setFromDate(startDate);
			setEndDate(today);

			setFilters((prevFilters: Dayjs | null) => ({
				...prevFilters,
				fromDate: startDate.valueOf(),
				endDate: today.valueOf(),
				dates: days,
			}));
		}
		setSelectedDateRange(days);
	};

	const handleSelectLocations = (data: any) => {
		resetErrors();
		setFilters((prevFilters: any) => ({
			...prevFilters,
			locations: data,
		}));
	};

	const setTestKitsBasedOnLocations = () => {
		const uniqueData = filters?.locations
			?.flatMap(({ testKitDescriptors }: any) => testKitDescriptors || [])
			?.filter(
				(v: any, i: number, a: any) => a?.findIndex((t: any) => t.id === v.id) === i
			);
		setTestKits?.([...uniqueData]);
	};

	const handleSelectKits = (data: any) => {
		resetErrors();
		setFilters((prevFilters: any) => ({
			...prevFilters,
			testKits: data,
		}));
	};

	useEffect(() => {
		// if (locations.length && !filters.locations.length) {
			handleSelectLocations(locations);
		// }
		// if (testKits.length && !filters.testKits.length) {
			handleSelectKits(testKits);
		// }
	}, []);

	useEffect(() => {
		setTestKitsBasedOnLocations();
	}, [filters.locations]);

	return (
		<div
			className="grid grid-cols-12
              font-inter font-[600] text-[14px] tracking-[0.15px] leading-[17px] text-[#666666]
              pb-[8px]"
		>
			<div className="col-span-5">
				<div className="flex flex-col">
					<div className="mb-[8px]">Locations</div>
					<LocationsMultiSelect
						data={locations}
						selectedData={filters.locations}
						handleSelectedData={handleSelectLocations}
						error={filterErrors.locations}
						size="small"
					/>
				</div>
				<div className="flex flex-col mt-[20px]">
					<div className="mb-[8px]">Test Kits</div>
					<TestKitsDropDownMultiselect
						data={testKits}
						selectedData={filters.testKits}
						handleSelectedData={handleSelectKits}
						error={filterErrors.testKits}
						size="small"
					/>
				</div>
			</div>
			<div className="col-span-6">
				<div className="flex flex-col mb-[15px]">
					<div>Date Range</div>
					<div>
						<RadioButtonsGroup
							radioButtons={radioButtons}
							onChange={handleCheckBoxChange}
							defaultValue={selectedDateRange.toString()}
						/>
					</div>
				</div>
				<div className="flex flex-col">
					<div className="mb-[15px]"></div>
					<div className="flex flex-row gap-[20px]">
						<ExportDatePicker
							label="Start Date"
							value={fromDate}
							onChange={handleFromDateChange}
							addStyle="!mb-8 !w-[275px]"
						/>
						<ExportDatePicker
							label="End Date"
							value={endDate}
							onChange={handleEndDateChange}
							addStyle="!mb-8 !w-[275px]"
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ExportResultsFilter;